import React from "react";
import { BiSearch, BiCodeAlt, BiLayer, BiRocket, BiBulb } from "@acme/ui";
import { FooterSite, HeaderSite, LandingFaq, LandingFeatures, LandingHero, LandingModelSupport } from "@acme/feature";
import Head from "next/head";

const ChatgptApiUiFreePage = () => {
    const features = [
        {
            title: "Cost Effective",
            description: "Pay only for what you use directly to OpenAI. No markup.",
            icon: <BiRocket className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800/20"
        },
        {
            title: "No Monthly Fees",
            description: "DapperGPT basic plan is free to use with your key.",
            icon: <BiCodeAlt className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-purple-50 dark:bg-purple-900/10 border-purple-100 dark:border-purple-800/20"
        },
        {
            title: "Secure",
            description: "Your API key is stored locally in your browser.",
            icon: <BiSearch className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-800/20"
        },
        {
            title: "Rich Features",
            description: "Get features like folders, notes, and search that aren't available in standard API playgrounds.",
            icon: <BiLayer className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-orange-50 dark:bg-orange-900/10 border-orange-100 dark:border-orange-800/20"
        },
        {
            title: "Custom Tools and MVPs",
            description: "Extend ChatGPT with your own tools, APIs, and micro-plugins.",
            icon: <BiBulb className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-yellow-50 dark:bg-yellow-900/10 border-yellow-100 dark:border-yellow-800/20"
        },
        {
            title: "Agent",
            description: "Create multi-step AI workflows that can think, plan, and execute tasks.",
            icon: <BiRocket className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-indigo-50 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-800/20"
        },
    ];

    const faqList = [
        { "question": "Is the UI really free?", "answer": "Yes, DapperGPT is free to use. You only pay OpenAI for the API usage." },
        { "question": "How do I get an API key?", "answer": "You can generate an API Key from the OpenAI Platform dashboard." },
        { "question": "Which models are supported?", "answer": "We support OpenAI chat models and related modalities (like vision and image generation) depending on the provider features enabled for your account." },
        { "question": "Do you store my API key?", "answer": "Your API key is stored locally in your browser/device. We don't require you to send it to our servers." },
        { "question": "Is this an alternative to the OpenAI Playground?", "answer": "Yes\u2014it's a cleaner, more productive interface with folders, search, and reusable prompts." },
        { "question": "Can I use it for teams?", "answer": "Yes. DapperGPT works great for individuals and teams who want consistent workflows and shared prompts." }
    ]

    return (
        <>
            <Head>
                <title>ChatGPT API UI - Free Interface | DapperGPT</title>
                <meta name="description" content="Use a modern ChatGPT API UI with your own OpenAI key. Organize chats, save prompts, and pay only for API usage." />
                <meta property="og:title" content="ChatGPT API UI - Free Interface" />
                <meta property="og:description" content="A modern ChatGPT API UI for your OpenAI API key." />
            </Head>
            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col font-sans text-gray-900 dark:text-gray-100 selection:bg-blue-500/30 transition-colors duration-300">
                <HeaderSite containerClassName="!max-w-7xl px-6" />

                <main className="flex-grow">
                    <LandingHero
                        title="Free UI for ChatGPT API"
                        subtitle="Don't pay for a subscription if you have an API key. Connect your OpenAI key to DapperGPT and pay only for what you use."
                        badge="BYO Key Interface"
                        heroImage="/page/chatgpt-api-ui.png"
                        color="blue"
                    />
                    <LandingFeatures
                        title="Why use our UI?"
                        subtitle="A premium experience without the premium price tag."
                        features={features}
                    />
                    <LandingModelSupport title="Works with">
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-serif text-gray-900 dark:text-gray-100">OpenAI Chat</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold text-gray-900 dark:text-gray-100">Vision Models</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-sans text-gray-900 dark:text-gray-100">Image Generation</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-mono text-gray-900 dark:text-gray-100">Function / Tool Calling</span></div>
                    </LandingModelSupport>
                    <LandingFaq items={faqList} />
                </main>

                <FooterSite />
            </div>
        </>
    );
};

export default ChatgptApiUiFreePage;
